/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Menu, Popup } from '@mairistem/composants';
import { FicheBar } from '@mairistem/fiche';

import SaisieEvenementModal from '@/components/Dashboard/Blocs/Creation/SaisieEvenementModal';
import { baseUrlApp } from '@/routes';
import type { TypeActe } from '@/types';

interface ActeFicheBarProps {
  identifiant?: number | string;
  /** Type d'acte, qui compose l'URL des documents. */
  typeActe: TypeActe;
}

/**
 * Barre d'actions de la fiche d'un acte.
 *
 * Même montage que `RecenseFicheBar` de recensement-citoyen : la barre n'est
 * pas rendue par `FicheContainer`, c'est la fiche qui l'ajoute via la render
 * prop, et les boutons propres à l'application sont passés en `children` de
 * `FicheBar` — ils s'affichent après les boutons standards.
 *
 * Le bouton « valise » diffère de celui de recensement sur un point : là-bas il
 * ouvre une modale, ici il navigue vers un écran plein à deux onglets (liste
 * des documents et annotation).
 */
const ActeFicheBar = (props: ActeFicheBarProps) => {
  const { identifiant, typeActe, ...rest } = props;

  const history = useHistory();

  /**
   * L'écran des documents a sa propre `Feature` (`/documents/{type}/{id}`), il
   * ne vit pas sous l'URL de la fiche : les `Feature` de `@mairistem/module`
   * sont déclaratives — c'est le shell 360 qui monte celle dont le `path`
   * correspond à l'URL — donc une URL sans feature déclarée n'affiche rien.
   */
  const handleDocuments = React.useCallback(() => {
    history.push(`${baseUrlApp}documents/${typeActe}/${identifiant}`);
  }, [history, typeActe, identifiant]);

  const [openSaisieEvenement, setOpenSaisieEvenement] = React.useState(false);

  /**
   * Court-circuite le bouton natif « + » de `FicheBar` (qui ouvrirait sinon
   * une fiche vierge en place, sans passer par l'assistant de saisie) : au
   * lieu de créer directement, on ouvre la même modale que le dashboard, avec
   * le type d'acte de la fiche courante présélectionné. Retourner `false`
   * annule silencieusement la création par défaut (cf. `FicheBarButtonCreate`
   * de `@mairistem/fiche`) sans masquer/altérer le bouton natif.
   */
  const handleCreating = React.useCallback(() => {
    setOpenSaisieEvenement(true);
    return false as const;
  }, []);

  return (
    <FicheBar
      {...rest}
      key="ActeFicheBar"
      type="acte"
      // `FicheBar` exige un identifiant ; en création il n'y en a pas encore.
      identifiant={identifiant ?? ''}
      onCreating={handleCreating}
    >
      <Popup
        data-testid="fiche-bar-bouton-documents"
        key="ficheButtonDocuments"
        position="bottom left"
        trigger={
          <Menu.Item
            as={Button}
            className="fiche-bar-button"
            data-testid="fiche-bar-bouton-documents"
            icon="briefcase"
            style={{ marginLeft: '5rem' }}
            disabled={!identifiant}
            onClick={handleDocuments}
          />
        }
        content="Documents de l'acte"
      />
      <SaisieEvenementModal
        open={openSaisieEvenement}
        title="Séléction du type d'acte/décision/PACS à créer"
        initialTypeActe={typeActe}
        onClose={() => setOpenSaisieEvenement(false)}
      />
    </FicheBar>
  );
};

export default ActeFicheBar;
